import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Button } from '@tyndo/ui-kit';
import { viewRegistry } from '../registries';
import { viewSlugForUrl } from '../utils/urlHelpers';
import { recordError } from '../observability/telemetry';
import { useModuleAccess } from '../layout/ModuleAccessContext';
import { isModulePermitted } from '../layout/entitlementGate';

/**
 * Redirects bare /:moduleId and /:moduleId/view to the module's default view.
 */
// Max number of viewRegistry mutations the redirect will wait through before
// giving up: a federation failure can re-fire the subscription repeatedly (each
// failed Pass-2 upgrade bumps the revision) without ever landing a default
// view, which would otherwise re-arm the watchdog indefinitely and spin
// forever. After this many mutations with still no navigable default we
// force-navigate to /browse with a stable error code.
const MODULE_REDIRECT_MAX_MUTATIONS = 3;
// Grace window before the watchdog fires when no registry mutation is arriving.
const MODULE_REDIRECT_TIMEOUT_MS = 10000;
// Stable, machine-readable code carried on the /browse redirect so the
// destination (and telemetry) can distinguish a genuine "no views" bounce from
// an arbitrary navigation.
const MODULE_REDIRECT_NO_DEFAULT_VIEW_CODE = 'module_no_default_view';

/**
 * Bridges `/ebr/...` deep-links onto the Batch module's EBR views. The EBR
 * dashboard widgets carry a virtual `ebr` moduleId and drill down to `/ebr/...`
 * paths, but EBR's operator surface ships inside batch-ui (the gateway emits no
 * standalone `ebr` UI module), so the generic `/:moduleId/...` resolver finds
 * no views for the segment. Known sub-paths map onto the owning batch views;
 * the query string (e.g. `?filter=signature-gaps`) is preserved.
 */
export function EbrRouteBridge() {
 const location = useLocation();
 // Path after the /ebr prefix, e.g. 'review-queue', 'packages', ''.
 const sub = location.pathname.replace(/^\/ebr\/?/, '').replace(/\/+$/, '');
 // Package readiness lives on the Batch Record Workbench; everything else
 // (review-queue, signature gaps, bare /ebr) is the EBR review workbench.
 const target = sub === 'packages' ? '/tyndo.batch/records' : '/tyndo.batch/ebr-review';
 return <Navigate to={`${target}${location.search}`} replace />;
}

export function ModuleRedirect() {
 const { moduleId} = useParams<{ moduleId: string}>();
 const navigate = useNavigate();
 // Bare `/:moduleId` is a deep link too — gate it before it can redirect the
 // operator into a module's default view.
 const moduleAccess = useModuleAccess();
 const moduleAllowed = isModulePermitted(moduleAccess, moduleId);
 const accessResolved = moduleAccess.state !== 'loading';
 // Subscribe to ViewRegistry mutations so a redirect attempt that happened
 // while only Pass 1 (gateway-stub) views were registered is retried after
 // Pass 2 (federated module) lands the real view list. Without this, the
 // bare /:moduleId URL renders the spinner forever for v2 modules whose
 // gateway manifest ships `views: []` and the real list arrives later.
 const [registryRevision, setRegistryRevision] = useState(() => viewRegistry.getRevision());
 // Counts viewRegistry mutations observed since mount. A federation failure can
 // re-fire the subscription without ever landing a default view; bounding the
 // retries on this counter (vs. re-arming forever) is what stops the infinite
 // spin after a federation failure. A ref because the subscription callback
 // must read/update it without being re-created.
 const mutationCountRef = useRef(0);
 useEffect(() => {
 return viewRegistry.subscribe(() => {
 mutationCountRef.current += 1;
 setRegistryRevision(viewRegistry.getRevision());
 });
 }, []);

 // A module registered with an empty view list (e.g. a v2 surface whose
 // gateway manifest ships `views: []` and whose federated bundle never lands,
 // or one that genuinely has no views yet) would otherwise leave this redirect
 // spinning forever waiting for a defaultView that never arrives. After a grace
 // period we give up, tell the operator, and bounce them to /browse instead of
 // an infinite spinner.
 const [timedOut, setTimedOut] = useState(false);

 useEffect(() => {
 if (!moduleId || !moduleAllowed) return;

 // Find the default view for this module
 const allViews = viewRegistry.listViews();
 const moduleViews = allViews.filter((v) => v.moduleId === moduleId);
 const defaultView = moduleViews.find((v) => v.isDefault) || moduleViews[0];

 if (defaultView?.viewId) {
 // Clean URL form (no /view/ segment, no full viewId).
 navigate(`/${moduleId}/${viewSlugForUrl(defaultView.viewId)}`, { replace: true});
} else if (defaultView?.model && defaultView?.viewType) {
 navigate(`/${moduleId}/${defaultView.viewType}/${defaultView.model}`, { replace: true});
}
}, [moduleId, navigate, registryRevision, moduleAllowed]);

 // Watchdog: if no view has resolved within the grace window, the module has
 // no navigable default. Re-arms on each registry mutation so a late Pass-2
 // federated registration cancels the timeout before it fires — but only up to
 // MODULE_REDIRECT_MAX_MUTATIONS times. Past that, a federation failure that
 // keeps bumping the revision (without ever landing a default view) would
 // re-arm forever, so we trip the watchdog immediately instead of waiting out
 // another full grace window on every churned mutation.
 useEffect(() => {
 if (!moduleId || !moduleAllowed) return;
 const moduleViews = viewRegistry
 .listViews()
 .filter((v) => v.moduleId === moduleId);
 const hasDefault =
 moduleViews.some((v) => v.isDefault) || moduleViews.length > 0;
 if (hasDefault) {
 setTimedOut(false);
 return;
}
 // Exhausted the bounded retries with still no default view → give up now
 // rather than arming yet another grace window.
 if (mutationCountRef.current >= MODULE_REDIRECT_MAX_MUTATIONS) {
 setTimedOut(true);
 return;
}
 const timer = window.setTimeout(
 () => setTimedOut(true),
 MODULE_REDIRECT_TIMEOUT_MS,
 );
 return () => window.clearTimeout(timer);
}, [moduleId, registryRevision, moduleAllowed]);

 useEffect(() => {
 if (!timedOut || !moduleId) return;
 // Record the no-default-view outcome for telemetry, but DON'T fire a toast
 // and auto-bounce to /browse: that split the failure across two surfaces (a
 // transient toast AND a redirect away from the URL the operator typed). The
 // localized inline "No views available" state below is the single error
 // surface — same as the module ErrorBoundary's "Coming soon" — and offers an
 // explicit "Browse all modules" action the operator drives themselves.
 recordError(
 'module.redirect_no_default_view',
 new Error(`Module ${moduleId} registered no navigable default view`),
 { moduleId, code: MODULE_REDIRECT_NO_DEFAULT_VIEW_CODE, mutations: mutationCountRef.current },
 );
}, [timedOut, moduleId]);

 // Entitlement branches come first: a denied module must never redirect into
 // one of its views, and an unresolved gate must not either.
 if (!accessResolved) {
 return (
 <div className="flex items-center justify-center h-96">
 <div
 role="status"
 aria-live="polite"
 data-testid="moduleredirect-entitlements-loading"
 className="text-center"
 >
 <div className="animate-spin motion-reduce:animate-none rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4">
 <span className="sr-only">Loading…</span>
 </div>
 <p className="text-muted-foreground">Checking your access…</p>
 </div>
 </div>
 );
}

 if (!moduleAllowed) {
 return (
 <div className="flex items-center justify-center h-96">
 <div className="text-center max-w-sm px-6" data-testid="moduleredirect-module-forbidden">
 <p className="text-foreground font-medium mb-1">Not available</p>
 <p className="text-muted-foreground text-sm mb-4">
 You don't have access to this module.
 </p>
 <Button type="button" onClick={() => navigate('/browse', { replace: true })}>
 Browse your modules
 </Button>
 </div>
 </div>
 );
}

 if (timedOut) {
 return (
 <div className="flex items-center justify-center h-96">
 <div className="text-center max-w-sm px-6">
 <p className="text-foreground font-medium mb-1">No views available</p>
 <p className="text-muted-foreground text-sm mb-4">
 The {moduleId} module hasn't registered any views yet.
 </p>
 <Button
 type="button"
 onClick={() =>
 navigate(
 `/browse?error=${MODULE_REDIRECT_NO_DEFAULT_VIEW_CODE}&module=${encodeURIComponent(moduleId ?? '')}`,
 { replace: true },
 )
 }
 >
 Browse all modules
 </Button>
 </div>
 </div>
 );
}

 return (
 <div className="flex items-center justify-center h-96">
 <div className="text-center">
 <div role="status" aria-label="Loading" className="animate-spin motion-reduce:animate-none rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4">
 <span className="sr-only">Loading…</span>
 </div>
 <p className="text-muted-foreground">Loading module…</p>
 </div>
 </div>
 );
}
